import { tableStyles, DEFAULT_SELECTION_COLUMN_WIDTH, DEFAULT_SELECTION_COLUMN_INSET_PX, resultsFilterChipSx } from "../styles/shared/tableStyles";
// GenericDataTable.jsx
import React, { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TableSortLabel,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Button,
  Chip,
  Tooltip,
  IconButton,
  CircularProgress,
  useMediaQuery } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { toTimeValue } from "../utils/dateTime";
import {
  withPillButtonClassName } from "../utils/pillButtonStyles";
import {
  defaultCheckboxCheckedIcon as sharedDefaultCheckboxCheckedIcon,
  defaultCheckboxIcon as sharedDefaultCheckboxIcon,
  defaultCheckboxIndeterminateIcon as sharedDefaultCheckboxIndeterminateIcon,
  referenceCheckboxIcon as sharedReferenceCheckboxIcon,
  referenceCheckboxIndeterminateIcon as sharedReferenceCheckboxIndeterminateIcon,
  TABLE_CHECKBOX_SIZE } from "../utils/checkboxIcons";
import { MatchActionIcon } from "./ActionButtonIcons";
import { GenericDataTableContext } from "./GenericDataTableContext";
import { ReactComponent as LongLeftArrow } from "../assets/icons/long-left-arrow.svg"; // â¬… long arrow
import { ReactComponent as LongRightArrow } from "../assets/icons/long-right-arrow.svg"; // âž¡ long arrow

const DEFAULT_ROWS_PER_PAGE = 10;
const rowsPerPageOptions = [5, 10, 15, 25, 50, 100];
const MOBILE_ROWS_PER_PAGE = 4;
const GENERIC_DATA_TABLE_ICON_RULES = {
  singleWebsite: true,
  iconSize: 22,
  iconGap: "8px",
  suppressActionTypes: ["twitter", "x"],
  suppressDisabledActionTypes: ["linkedin"] };

function ResultsToolbarPlacement({ targetId, children }) {
  const [target, setTarget] = useState(() =>
    targetId && typeof document !== "undefined"
      ? document.getElementById(targetId)
      : null
  );

  useEffect(() => {
    setTarget(
      targetId && typeof document !== "undefined"
        ? document.getElementById(targetId)
        : null
    );
  }, [targetId]);

  return target ? createPortal(children, target) : children;
}

// ----- Custom checkbox icons (rounded square like the mockup) -----
const referenceCheckboxCheckedIcon = (
  <Box
    sx={tableStyles.checkedBox()}
  >
    <Box
      component="span"
      sx={tableStyles.checkmark()}
    />
  </Box>
);

// âœ… Icon to match the uploaded "Match Candidates" button
// Active filter pills shown beside the results count.

// ----- helpers for sorting -----
const SORTABLE_NUMBER_REGEX = /^-?\d+(?:\.\d+)?$/;
const SORTABLE_DATE_REGEX = /^\d{4}-\d{2}-\d{2}(?:[ T].*)?$/;

function normalizeSortValue(value) {
  if (value == null) return null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "boolean") return value ? 1 : 0;

  const text = String(value).trim();
  if (!text) return null;

  if (SORTABLE_NUMBER_REGEX.test(text)) {
    const numeric = Number(text);
    return Number.isFinite(numeric) ? numeric : text.toLowerCase();
  }

  if (SORTABLE_DATE_REGEX.test(text)) {
    const timestamp = toTimeValue(text);
    return Number.isFinite(timestamp) ? timestamp : text.toLowerCase();
  }

  return text.toLowerCase();
}

function getComparator(order, orderBy) {
  const normalizedOrder = order === "desc" ? "desc" : "asc";

  return (a, b) => {
    const left = normalizeSortValue(a?.[orderBy]);
    const right = normalizeSortValue(b?.[orderBy]);

    if (left == null && right == null) return 0;
    if (left == null) return 1;
    if (right == null) return -1;

    if (left < right) return normalizedOrder === "asc" ? -1 : 1;
    if (left > right) return normalizedOrder === "asc" ? 1 : -1;
    return 0;
  };
}

function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const cmp = comparator(a[0], b[0]);
    if (cmp !== 0) return cmp;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

// ---- pagination number helper ----
function createPaginationItems(currentPageIndex, pageCount) {
  const current = currentPageIndex + 1; // to 1-based
  const pages = [];

  if (pageCount <= 7) {
    for (let i = 1; i <= pageCount; i++) pages.push(i);
    return pages;
  }

  if (current <= 3) {
    pages.push(1, 2, 3, "...", pageCount - 2, pageCount - 1, pageCount);
    return pages;
  }

  if (current >= pageCount - 2) {
    pages.push(1, 2, 3, "...", pageCount - 2, pageCount - 1, pageCount);
    return pages;
  }

  pages.push(1, "...", current - 1, current, current + 1, "...", pageCount);
  return pages;
}

function resolveRowsPerPageOption(
  value,
  options = [],
  fallback = DEFAULT_ROWS_PER_PAGE
) {
  const normalizedOptions = Array.isArray(options)
    ? options.map((item) => Number(item)).filter((item) => Number.isFinite(item) && item > 0)
    : [];
  const normalizedValue = Number(value);
  if (Number.isFinite(normalizedValue) && normalizedOptions.includes(normalizedValue)) {
    return normalizedValue;
  }

  const normalizedFallback = Number(fallback);
  if (Number.isFinite(normalizedFallback) && normalizedOptions.includes(normalizedFallback)) {
    return normalizedFallback;
  }

  return normalizedOptions[0] || DEFAULT_ROWS_PER_PAGE;
}

function flattenTableCells(node) {
  if (node == null || typeof node === "boolean") return [];
  if (Array.isArray(node)) return node.flatMap(flattenTableCells);
  if (React.isValidElement(node) && node.type === React.Fragment) {
    return React.Children.toArray(node.props.children).flatMap(flattenTableCells);
  }
  return React.isValidElement(node) ? [node] : [];
}

/**
 * props:
 *  - columns: [{ id, label, numeric }]
 *  - rows: []
 *  - renderRow: (row) => <> <TableCell /> ... </>
 *  - bulkActionsTop, bulkActionsBottom: ReactNode
 *  - defaultOrderBy: string
 *  - defaultOrderDirection: "asc" | "desc"
 *  - includeSelectionColumn: bool
 *  - initialRowsPerPage: number
 *  - showResultsHeader: bool
 *  - resultsFilterPills: [{ key, label, onDelete, deleteLabel? }]
 *
 * âœ… NEW:
 *  - showMatchCandidates: bool (default false)
 *  - onMatchCandidatesClick: (event, selectedIds) => void
 */
const GenericDataTable = ({
  columns,
  rows,
  renderRow,
  getRowProps,
  bulkActionsTop,
  bulkActionsBottom,
  defaultOrderBy = "name",
  defaultOrderDirection = "asc",
  includeSelectionColumn = false,
  initialRowsPerPage = DEFAULT_ROWS_PER_PAGE,
  showResultsHeader = true,
  resultsToolbarTargetId,
  resultsFilterPills = [],
  resetPageSignal,
  onSelectionChange,
  selectedIds,
  onSelectedIdsChange,
  preserveSelectionAcrossPages = false,
  selectionDisabled = false,
  exportFields,
  rootSx,
  topBulkActionsWrapperSx,
  tableContainerSx,
  tableSx,
  selectionColumnWidth,
  selectionCellSx,
  selectionCellStyle,
  bottomBulkActionsWrapperSx,
  paginationBarSx,
  resultsTotalCount,
  serverSidePagination = false,
  serverPage = 0,
  serverRowsPerPage,
  onServerPageChange,
  onServerRowsPerPageChange,
  paginationLoading = false,
  visualVariant = "default",

  // âœ… Match Candidates option
  showMatchCandidates = false,
  onMatchCandidatesClick }) => {
  const tableContainerRef = useRef(null);
  const onServerPageChangeRef = useRef(onServerPageChange);
  const lastResetPageSignalRef = useRef(resetPageSignal);
  const previousRowsRef = useRef(rows);
  const isMobileLayout = useMediaQuery("(max-width:600px)");
  const normalizedDefaultOrderDirection =
    defaultOrderDirection === "desc" ? "desc" : "asc";
  const [order, setOrder] = useState(normalizedDefaultOrderDirection);
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [selected, setSelected] = useState([]);
  const [hoverTooltip, setHoverTooltip] = useState("");
  const [pendingServerPage, setPendingServerPage] = useState(null);
  const [pageSizeLoading, setPageSizeLoading] = useState(false);
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(() =>
    resolveRowsPerPageOption(initialRowsPerPage, rowsPerPageOptions, DEFAULT_ROWS_PER_PAGE)
  );
  const sortableColumns = useMemo(
    () => columns.filter((column) => column.id !== "actions" && column.sortable !== false),
    [columns]
  );
  const mobileSortColumn = sortableColumns.find((column) => column.id === orderBy)
    ? orderBy
    : sortableColumns[0]?.id || "";
  const resolvedServerRowsPerPage = Number(serverRowsPerPage);
  const effectiveRowsPerPage = isMobileLayout
    ? MOBILE_ROWS_PER_PAGE
    : serverSidePagination && Number.isFinite(resolvedServerRowsPerPage) && resolvedServerRowsPerPage > 0
      ? resolvedServerRowsPerPage
      : rowsPerPage;

  useEffect(() => {
    onServerPageChangeRef.current = onServerPageChange;
  }, [onServerPageChange]);

  useEffect(() => {
    setOrderBy(defaultOrderBy);
    setOrder(normalizedDefaultOrderDirection);
  }, [defaultOrderBy, normalizedDefaultOrderDirection]);
  const isReferenceVariant = visualVariant === "reference";
  const isCompanyStandardVariant = visualVariant === "company-standard";
  const selectionIcons = isReferenceVariant
    ? {
        icon: sharedReferenceCheckboxIcon,
        checkedIcon: referenceCheckboxCheckedIcon,
        indeterminateIcon: sharedReferenceCheckboxIndeterminateIcon }
    : {
        icon: sharedDefaultCheckboxIcon,
        checkedIcon: sharedDefaultCheckboxCheckedIcon,
        indeterminateIcon: sharedDefaultCheckboxIndeterminateIcon };

  useEffect(() => {
    setRowsPerPage((current) =>
      resolveRowsPerPageOption(current, rowsPerPageOptions, initialRowsPerPage)
    );
  }, [initialRowsPerPage]);

  // Server-paginated tables need four records for the mobile card layout.
  // Do not re-send that size after a loading remount: the parent already uses
  // it, and its rows-per-page handler resets the active page to page one.
  useEffect(() => {
    if (!serverSidePagination) return;

    if (
      Number.isFinite(resolvedServerRowsPerPage) &&
      resolvedServerRowsPerPage > 0 &&
      resolvedServerRowsPerPage === effectiveRowsPerPage
    ) {
      return;
    }

    onServerRowsPerPageChange?.(effectiveRowsPerPage);
    onServerPageChange?.(0);
  }, [
    effectiveRowsPerPage,
    onServerPageChange,
    onServerRowsPerPageChange,
    resolvedServerRowsPerPage,
    serverSidePagination,
  ]);

  useEffect(() => {
    const previousSignal = lastResetPageSignalRef.current;
    lastResetPageSignalRef.current = resetPageSignal;

    // A server table already owns its initial page. Reset only after an
    // actual filter/tab signal change; running this on render was sending
    // a freshly selected page back to page one.
    if (previousSignal === resetPageSignal) return;
    if (serverSidePagination) onServerPageChangeRef.current?.(0);
    else setPage(0);
  }, [resetPageSignal, serverSidePagination]);

  // Locally filtered rows are the full matching result set. An unfiltered
  // server total must not override their count in the header or page range.
  const hasServerTotal = resultsTotalCount != null && resultsTotalCount !== "" &&
    Number.isFinite(Number(resultsTotalCount));
  const totalCount = serverSidePagination && hasServerTotal
    ? Math.max(0, Number(resultsTotalCount))
    : rows.length;
  const displayTotalCount = totalCount;

  // ----- sorting & paging -----
  const sortedRows = useMemo(
    () => stableSort(rows, getComparator(order, orderBy)),
    [rows, order, orderBy]
  );

  const paginatedRows = useMemo(
    () =>
      serverSidePagination
        ? sortedRows
        : sortedRows.slice(
            page * effectiveRowsPerPage,
            page * effectiveRowsPerPage + effectiveRowsPerPage
          ),
    [sortedRows, page, effectiveRowsPerPage, serverSidePagination]
  );

  const renderRowCells = (row) =>
    flattenTableCells(renderRow(row)).map((cell, index) =>
      React.cloneElement(cell, {
        "data-column-label": columns[index]?.label || "",
        "data-column-id": columns[index]?.id || "",
        // Keep every action control directly beneath the right-aligned
        // Actions header, regardless of how an individual table rendered it.
        align: columns[index]?.id === "actions" ? "right" : cell.props.align,
        style:
          tableStyles.actionCellInline({ columns, index, cell }),
        sx:
          tableStyles.actionCell({ columns, index, cell }),
        key: cell.key || columns[index]?.id || index })
    );

  // Keep narrow table views readable without restoring horizontal scrolling:
  // expose the complete value only when the rendered element is actually cut
  // off. Explicit titles provided by a caller remain untouched.
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return undefined;

    const syncTruncationTooltips = () => {
      const candidates = container.querySelectorAll("td *, th *");
      candidates.forEach((element) => {
        // Remove titles created by the earlier native-tooltip implementation.
        if (element.dataset.autoTruncationTitle) {
          element.removeAttribute("title");
        }
        if (element.hasAttribute("title") && !element.dataset.autoTruncationTitle) {
          return;
        }

        const fullText = String(element.textContent || "").trim();
        const isTruncated = element.scrollWidth > element.clientWidth + 1;

        if (isTruncated && fullText) {
          element.dataset.autoTruncationTitle = fullText;
        } else if (element.dataset.autoTruncationTitle) {
          delete element.dataset.autoTruncationTitle;
        }
      });
    };

    const frameId = window.requestAnimationFrame(syncTruncationTooltips);
    const observer = new ResizeObserver(syncTruncationTooltips);
    observer.observe(container);

    return () => {
      window.cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [columns, paginatedRows]);

  const pageCount = Math.max(1, Math.ceil(totalCount / effectiveRowsPerPage));
  const activePage = serverSidePagination ? Math.max(0, Number(serverPage) || 0) : page;
  const isPaginationLoading = Boolean(paginationLoading) || pendingServerPage !== null || pageSizeLoading;
  const displayStartIndex =
    displayTotalCount === 0 ? 0 : activePage * effectiveRowsPerPage + 1;
  const displayEndIndex =
    displayTotalCount === 0
      ? 0
      : Math.min(displayTotalCount, (activePage + 1) * effectiveRowsPerPage);

  useEffect(() => {
    if (serverSidePagination) return;
    setPage((prev) => Math.min(prev, pageCount - 1));
  }, [pageCount, serverSidePagination]);

  // A filtering action can reduce a server-backed result set from many pages
  // to one. Keep the parent-owned page in range so every table, tab panel,
  // and dialog shows the real selected page instead of an empty stale page.
  useEffect(() => {
    if (!serverSidePagination || activePage < pageCount) return;
    onServerPageChangeRef.current?.(pageCount - 1);
  }, [activePage, pageCount, serverSidePagination]);

  useEffect(() => {
    const receivedNewRows = previousRowsRef.current !== rows;
    previousRowsRef.current = rows;
    if (serverSidePagination && pendingServerPage !== null && receivedNewRows) {
      setPendingServerPage(null);
      setPageSizeLoading(false);
    }
  }, [rows, pendingServerPage, serverSidePagination]);

  useEffect(() => {
    if (serverSidePagination || !pageSizeLoading) return undefined;
    // Client-paginated tables update synchronously. Keep the feedback visible
    // long enough for the page-size change to be perceptible.
    const timer = window.setTimeout(() => setPageSizeLoading(false), 220);
    return () => window.clearTimeout(timer);
  }, [pageSizeLoading, serverSidePagination]);

  // ----- handlers -----
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getRowId = (r) =>
    r?.id ?? r?.candidate_id ?? r?.job_id ?? r?.client_id ?? r?.lead_id ?? r?.name;
  const normalizeSelectionId = (id) => String(id ?? "");

  useEffect(() => {
    if (!Array.isArray(selectedIds)) return;
    setSelected(selectedIds.map(normalizeSelectionId).filter(Boolean));
  }, [selectedIds]);

  const emitSelection = (nextSelected) => {
    const normalized = (Array.isArray(nextSelected) ? nextSelected : [])
      .map(normalizeSelectionId)
      .filter(Boolean);

    setSelected(normalized);
    if (onSelectionChange) onSelectionChange(normalized);
    if (onSelectedIdsChange) onSelectedIdsChange(normalized);
  };

  useEffect(() => {
    if (preserveSelectionAcrossPages) return;
    setSelected((prev) => {
      const current = (Array.isArray(prev) ? prev : []).map(normalizeSelectionId).filter(Boolean);
      const validIds = new Set(rows.map((row) => normalizeSelectionId(getRowId(row))));
      const nextSelected = current.filter((id) => validIds.has(id));

      if (nextSelected.length === current.length) return prev;

      if (onSelectionChange) onSelectionChange(nextSelected);
      if (onSelectedIdsChange) onSelectedIdsChange(nextSelected);
      return nextSelected;
    });
  }, [rows, onSelectionChange, onSelectedIdsChange, preserveSelectionAcrossPages]);

  const handleSelectAllClick = (event) => {
    if (!includeSelectionColumn) return;

    const visibleIds = paginatedRows
      .map((row) => normalizeSelectionId(getRowId(row)))
      .filter(Boolean);
    const visibleIdSet = new Set(visibleIds);

    if (event.target.checked) {
      emitSelection([...new Set([...selected, ...visibleIds])]);
      return;
    }
    emitSelection(selected.filter((id) => !visibleIdSet.has(id)));
  };

  const handleToggleRow = (id) => {
    const normalizedId = normalizeSelectionId(id);
    const selectedIndex = selected.indexOf(normalizedId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, normalizedId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    emitSelection(newSelected);
  };

  const isSelected = (id) => selected.indexOf(normalizeSelectionId(id)) !== -1;

  const handleChangeRowsPerPage = (event) => {
    const value = resolveRowsPerPageOption(
      parseInt(event.target.value, 10),
      rowsPerPageOptions,
      initialRowsPerPage
    );
    setRowsPerPage(value);
    setPageSizeLoading(true);
    if (serverSidePagination) {
      // Changing the page size also asks the server for a new result set.
      // Keep the shared pagination indicator visible until those rows arrive.
      setPendingServerPage(0);
      onServerRowsPerPageChange?.(value);
      onServerPageChange?.(0);
    } else {
      setPage(0);
    }
  };

  const handlePrevPage = () => {
    if (activePage <= 0) return;
    if (serverSidePagination) {
      setPendingServerPage(activePage - 1);
      onServerPageChange?.(activePage - 1);
    }
    else setPage(activePage - 1);
  };

  const handleNextPage = () => {
    if (activePage >= pageCount - 1) return;
    if (serverSidePagination) {
      setPendingServerPage(activePage + 1);
      onServerPageChange?.(activePage + 1);
    }
    else setPage(activePage + 1);
  };

  const handlePageNumberClick = (nextPage) => {
    if (nextPage === activePage) return;
    if (serverSidePagination) {
      setPendingServerPage(nextPage);
      onServerPageChange?.(nextPage);
    } else {
      setPage(nextPage);
    }
  };

  const leftDisabled = activePage === 0 || totalCount === 0;
  const rightDisabled = activePage >= pageCount - 1 || totalCount === 0;

  const paginationItems = createPaginationItems(activePage, pageCount);
  const resolvedSelectionColumnWidth = Number.isFinite(Number(selectionColumnWidth))
    ? Math.max(0, Number(selectionColumnWidth))
    : isReferenceVariant
    ? 36
    : DEFAULT_SELECTION_COLUMN_WIDTH;
  const resolvedSelectionColumnInsetPx = Math.max(
    0,
    Math.min(
      DEFAULT_SELECTION_COLUMN_INSET_PX,
      resolvedSelectionColumnWidth - TABLE_CHECKBOX_SIZE
    )
  );
  const headerSelectionCellSx = tableStyles.headerSelectionCell({ resolvedSelectionColumnWidth, resolvedSelectionColumnInsetPx });
  const bodySelectionCellSx = tableStyles.bodySelectionCell({ resolvedSelectionColumnWidth, resolvedSelectionColumnInsetPx });
  const headerSelectionCellStyle = tableStyles.headerSelectionInline({ resolvedSelectionColumnWidth, resolvedSelectionColumnInsetPx, selectionCellStyle });
  const bodySelectionCellStyle = tableStyles.bodySelectionInline({ resolvedSelectionColumnWidth, resolvedSelectionColumnInsetPx, selectionCellStyle });

  // âœ… Match Candidates button config (looks like uploaded image)
  const matchBtn = useMemo(() => {
    if (!showMatchCandidates) return null;

    return {
      key: "matchCandidates",
      label: "Match Candidates",
      confirm: true,
      confirmLabel: "Match Candidates",
      onClick: (e) => {
        if (typeof onMatchCandidatesClick === "function") {
          onMatchCandidatesClick(e, selected);
        }
      } };
  }, [showMatchCandidates, onMatchCandidatesClick, selected]);

  // âœ… shared renderer: inject into BulkActionsBar (rightButton) OR wrap any node with right button
  const renderBulkWithMatch = (node) => {
    if (!node) return null;

    const withExportContext = (element) =>
      React.isValidElement(element)
        ? React.cloneElement(element, {
            // Keep the exporter preview aligned with this table's selection
            // contract.  Pages do not all expose the same primary-key name.
            exportContext: { columns, fields: exportFields, rows, selectedIds: selected, getRowId },
            selectedCount: selected.length })
        : element;

    const contextualNode = withExportContext(node);

    // if not enabled, render as-is
    if (!showMatchCandidates || !matchBtn) return contextualNode;

    // try to inject rightButton if this is an element that supports it
    if (React.isValidElement(contextualNode)) {
      const existingRight = contextualNode.props?.rightButton;

      // If the element already has rightButton prop OR is named BulkActionsBar, clone-inject
      const supportsRightButton =
        Object.prototype.hasOwnProperty.call(contextualNode.props || {}, "rightButton") ||
        contextualNode.type?.name === "BulkActionsBar";

      if (supportsRightButton) {
        return React.cloneElement(contextualNode, {
          rightButton: {
            ...(existingRight || {}),
            key: existingRight?.key || matchBtn.key,
            label: existingRight?.label || matchBtn.label,
            onClick: existingRight?.onClick || matchBtn.onClick,
            confirm:
              typeof existingRight?.confirm === "boolean"
                ? existingRight.confirm
                : matchBtn.confirm,
            confirmLabel: existingRight?.confirmLabel || matchBtn.confirmLabel,
            sx: tableStyles.matchActionOverride({ existingRight }) } });
      }
    }

    // fallback: wrap the provided node (left) and add Match button on the right
    return (
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="center"
        gap={1.5}
      >
        <Box sx={tableStyles.toolbarContent()}>{contextualNode}</Box>

        <Button
          size="small"
          variant="contained"
          disableElevation
          className={withPillButtonClassName()}
          startIcon={<MatchActionIcon size={24} />}
          onClick={matchBtn.onClick}
          sx={tableStyles.matchButton()}
        >
          {matchBtn.label}
        </Button>
      </Box>
    );
  };

  // âœ… TOP bulk actions (unchanged structure, just rendered through helper)
  const bulkTopRendered = useMemo(
    () => renderBulkWithMatch(bulkActionsTop),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bulkActionsTop, exportFields, showMatchCandidates, matchBtn]
  );

  // âœ… BOTTOM bulk actions: NOW includes Match Candidates too (requested)
  const bulkBottomRendered = useMemo(
    () => renderBulkWithMatch(bulkActionsBottom),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bulkActionsBottom, exportFields, showMatchCandidates, matchBtn]
  );

  return (
    <GenericDataTableContext.Provider value={GENERIC_DATA_TABLE_ICON_RULES}>
      <Box
        className="generic-data-table"
        sx={tableStyles.root({ rootSx })}
      >
      {/* RESULTS / VIEW / RANGE HEADER (top) */}
      {showResultsHeader && (
        <ResultsToolbarPlacement targetId={resultsToolbarTargetId}>
        <Box
          mb={1}
          mt="10px"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          className="side-padding-16px top-results-bar "
          sx={
            tableStyles.resultsBar ({ isReferenceVariant })
          }
        >
          <Box
            display="flex"
            alignItems="center"
            gap={1.25}
            flexWrap="wrap"
            sx={tableStyles.resultsSummary({ resultsToolbarTargetId })}
          >
            {/* Results count */}
            <Typography className="results-count" variant="body2" color="text.secondary">
              Results:{" "}
              <Box component="span" sx={tableStyles.resultsCount()}>
                {displayTotalCount}
              </Box>
            </Typography>

            {!!resultsFilterPills.length && (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
                sx={tableStyles.filterChips()}
              >
                {resultsFilterPills.map((pill) => (
                  <Chip
                    key={pill.key || pill.label}
                    label={pill.label}
                    onDelete={pill.onDelete}
                    deleteIcon={<CancelRoundedIcon />}
                    aria-label={pill.deleteLabel || `Remove filter ${pill.label}`}
                    sx={resultsFilterChipSx}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Box
            display="flex"
            className="top-tbl-filters-container"
            alignItems="center"
            gap={2}
            sx={tableStyles.filterControls()}
          >
            {/* View dropdown */}
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                View:
              </Typography>
              <Box
                sx={tableStyles.mobilePageSize()}
              >
                {MOBILE_ROWS_PER_PAGE}
              </Box>
              <FormControl size="small" sx={tableStyles.desktopPageSize()}>
                <Select
                  value={serverSidePagination ? effectiveRowsPerPage : rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  sx={tableStyles.pageSizeSelect({ isReferenceVariant })}
                >
                  {rowsPerPageOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Range text */}
            <Box className="top-results-range" display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" color="text.secondary">
                {displayStartIndex} - {displayEndIndex} of {displayTotalCount}
              </Typography>

              {/* Small arrows (top) */}
              <Box display="flex" className="arrow-btns" alignItems="center" gap={1}>
                <Button
                  className="top-arrow-btn"
                  size="small"
                  type="button"
                  onClick={handlePrevPage}
                  disabled={leftDisabled}
                  sx={tableStyles.topPreviousButton({ isReferenceVariant })}
                >
                  <ArrowBackIosNewIcon sx={tableStyles.topPreviousIcon({ isReferenceVariant })} />
                </Button>
                <Button
                  size="small"
                  type="button"
                  onClick={handleNextPage}
                  className="top-arrow-btn"
                  disabled={rightDisabled}
                  sx={tableStyles.topNextButton({ isReferenceVariant })}
                >
                  <ArrowForwardIosIcon sx={tableStyles.topNextIcon({ isReferenceVariant })} />
                </Button>
              </Box>
              </Box>
          </Box>
        </Box>
        </ResultsToolbarPlacement>
      )}

      {/* TOP BULK ACTIONS */}
      {bulkTopRendered && (
        <Box
          className="bulkactions"
          sx={tableStyles.topBulkActions({ isReferenceVariant, topBulkActionsWrapperSx })}
        >
          {bulkTopRendered}
        </Box>
      )}

      {/* Table headers are hidden when rows become cards on small screens. */}
      {sortableColumns.length > 0 && (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          className="generic-data-table-mobile-sort"
          sx={tableStyles.mobileSortBar()}
        >
          <Typography variant="body2" color="text.secondary" sx={tableStyles.mobileSortLabel()}>
            Sort by:
          </Typography>
          <FormControl size="small" sx={tableStyles.mobileSortField()}>
            <Select
              value={mobileSortColumn}
              onChange={(event) => handleRequestSort(event.target.value)}
              inputProps={{ "aria-label": "Sort table by" }}
              sx={tableStyles.mobileSortSelect()}
            >
              {sortableColumns.map((column) => (
                <MenuItem key={column.id} value={column.id}>
                  {column.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            size="small"
            onClick={() => setOrder((current) => (current === "asc" ? "desc" : "asc"))}
            aria-label={`Sort ${order === "asc" ? "descending" : "ascending"}`}
            sx={tableStyles.mobileSortDirection()}
          >
            {order === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
          </IconButton>
        </Box>
      )}

      {/* TABLE */}
      <Tooltip
        title={hoverTooltip}
        open={Boolean(hoverTooltip)}
        followCursor
        enterDelay={120}
        disableFocusListener
        disableTouchListener
        placement="bottom"
        slotProps={{
          tooltip: {
            sx: tableStyles.pageSizeMenu() } }}
      >
        <TableContainer
          ref={tableContainerRef}
          className="generic-tbl"
          onMouseMove={(event) => {
            const target = event.target instanceof Element ? event.target : null;
            const textElement = target?.closest("[data-auto-truncation-title]");
            const nextTooltip = String(
              textElement?.getAttribute("data-auto-truncation-title") ||
                ""
            ).trim();
            setHoverTooltip((current) => (current === nextTooltip ? current : nextTooltip));
          }}
          onMouseLeave={() => setHoverTooltip("")}
          sx={tableStyles.tableContainer({ tableContainerSx })}
        >
        <Table
          size="small"
          sx={tableStyles.table({ isReferenceVariant, isCompanyStandardVariant, tableSx })}
        >
          <colgroup>
            {includeSelectionColumn ? (
              <col
                style={tableStyles.selectionColumn({ resolvedSelectionColumnWidth })}
              />
            ) : null}
            {columns.map((col) => (
              <col
                key={col.id}
                style={tableStyles.dataColumn({ col })}
              />
            ))}
          </colgroup>
          <TableHead>
              <TableRow borderTop={isReferenceVariant ? "none" : "solid 1px #D2D8DE"}>
              {includeSelectionColumn && (
                <TableCell
                  padding="checkbox"
                  className="generic-table-selection-cell"
                  sx={tableStyles.headerSelection({ headerSelectionCellSx, selectionCellSx })}
                  style={headerSelectionCellStyle}
                >
                  <Checkbox
                    disabled={selectionDisabled}
                    inputProps={{ "aria-label": "Select all rows on this page" }}
                    color="primary"
                    indeterminate={
                      paginatedRows.some((row) => isSelected(getRowId(row))) &&
                      !paginatedRows.every((row) => isSelected(getRowId(row)))
                    }
                    checked={
                      paginatedRows.length > 0 &&
                      paginatedRows.every((row) => isSelected(getRowId(row)))
                    }
                    // âœ… stop bubbling so header clicks don't trigger other handlers
                    onClick={(e) => e.stopPropagation()}
                    onChange={handleSelectAllClick}
                    icon={selectionIcons.icon}
                    checkedIcon={selectionIcons.checkedIcon}
                    indeterminateIcon={selectionIcons.indeterminateIcon}
                  />
                </TableCell>
              )}

              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  sortDirection={orderBy === col.id ? order : "asc"}
                  align={col.id === "actions" ? "right" : "left"}
                  sx={tableStyles.headerCell({ col, isReferenceVariant })}
                >
                  {col.id === "actions" || col.sortable === false ? (
                    col.id === "actions" ? (
                      <TableSortLabel
                        active={false}
                        hideSortIcon
                        sx={tableStyles.unsortableLabel()}
                      >
                        {col.label || "Actions"}
                      </TableSortLabel>
                    ) : col.label
                  ) : (
                  <TableSortLabel
                    active={true} // ðŸ”¹ ALWAYS active â†’ always show icon
                    hideSortIcon={false} // ðŸ”¹ forces icon to display
                    direction={orderBy === col.id ? order : "asc"}
                    onClick={() => handleRequestSort(col.id)}
                    sx={tableStyles.sortLabel({ col })}
                  >
                    <span className="table-header-label">{col.label}</span>
                  </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRows.map((row) => {
              const rowId = getRowId(row);
              const selectedRow = isSelected(rowId);
              const extraRowProps =
                typeof getRowProps === "function" && row ? getRowProps(row) || {} : {};

              return (
                <TableRow
                  key={rowId}
                  hover
                  role="checkbox"
                  aria-checked={selectedRow}
                  selected={selectedRow}
                  data-id={rowId} // âœ… ADD: allows DOM fallback extraction too
                  onClick={extraRowProps.onClick}
                  sx={extraRowProps.sx}
                  {...extraRowProps}
                >
                  {includeSelectionColumn && (
                    <TableCell
                      padding="checkbox"
                      className="generic-table-selection-cell"
                      sx={tableStyles.bodySelection({ bodySelectionCellSx, selectionCellSx })}
                      style={bodySelectionCellStyle}
                    >
                      <Checkbox
                        disabled={selectionDisabled}
                        color="primary"
                        checked={selectedRow}
                        inputProps={{ "aria-label": `Select ${row.full_name || row.name || rowId}` }}
                        value={rowId} // âœ… ADD: checkbox now carries row id
                        icon={selectionIcons.icon}
                        checkedIcon={selectionIcons.checkedIcon}
                        indeterminateIcon={selectionIcons.indeterminateIcon}
                        // âœ… stop bubbling; but do NOT preventDefault
                        onClick={(e) => e.stopPropagation()}
                        // âœ… toggle selection ONLY here
                        onChange={() => handleToggleRow(rowId)}
                      />
                    </TableCell>
                  )}

                  {renderRowCells(row)}
                </TableRow>
              );
            })}

            {paginatedRows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (includeSelectionColumn ? 1 : 0)}
                  align="center"
                >
                  <Typography variant="body2" color="text.secondary" sx={tableStyles.emptyState()}>
                    No results found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </TableContainer>
      </Tooltip>

      {/* BOTTOM BULK ACTIONS (âœ… NOW includes Match Candidates) */}
      {bulkBottomRendered && (
        <Box
          className="bulkactions"
          sx={tableStyles.bottomBulkActions({ isReferenceVariant, bottomBulkActionsWrapperSx })}
        >
          {bulkBottomRendered}
        </Box>
      )}

      {/* BOTTOM PAGINATION BAR (Prev / numbers / Next) */}
      <Box
        mt={1.5}
        mb={0.5}
        display="grid"
        alignItems="center"
        className="side-padding-16px tbl-pagination-bar"
        marginTop={5}
        marginBottom={5}
        position="relative"
        sx={
          tableStyles.paginationBar({ isReferenceVariant, paginationBarSx })
        }
      >
        {isPaginationLoading ? (
          <Box
            aria-live="polite"
            sx={tableStyles.paginationLoading()}
          >
            <CircularProgress
              aria-label="Loading page"
              size={18}
              thickness={4.5}
              sx={tableStyles.loadingSpinner()}
            />
          </Box>
        ) : null}
        {/* LEFT button (Prev) */}
        <Button
          type="button"
          onClick={handlePrevPage}
          disabled={leftDisabled}
          className="prev-btn"
          sx={tableStyles.previousButton({ isReferenceVariant })}
        >
          <LongLeftArrow width={20} height={20} />
          Prev
        </Button>

        {/* CENTER: page numbers */}
        <Box className="pagination-page-numbers" display="flex" alignItems="center" justifyContent="center" gap={1}>
          {paginationItems.map((item, idx) =>
            typeof item === "number" ? (
              <Button
                key={item}
                type="button"
                onClick={() => handlePageNumberClick(item - 1)}
                disabled={totalCount === 0}
                sx={tableStyles.pageButton({ isReferenceVariant, activePage, item })}
              >
                {item}
              </Button>
            ) : (
              <Typography key={`ellipsis-${idx}`} variant="body2" color="text.secondary" sx={tableStyles.pageEllipsis({ isReferenceVariant })}>
                {item}
              </Typography>
            )
          )}
        </Box>

        {/* RIGHT button (Next) */}
        <Button
          type="button"
          onClick={handleNextPage}
          disabled={rightDisabled}
          className="next-btn"
          sx={tableStyles.nextButton({ isReferenceVariant })}
        >
          Next
          <LongRightArrow width={20} height={20} />
        </Button>
      </Box>

      </Box>
    </GenericDataTableContext.Provider>
  );
};

export default GenericDataTable;
